import moment from 'moment';
import { ALIGNMENT_FILTER_STATUS } from '../../constants/okr/alignmentFilter.constants';
import { okrDetailConstants } from '../../constants/okr/okrDetails.constants';
import { mapTimePeriodCriterionToParams } from '../../utils/timePeriodFilter.util';

/** Matches search-api's ObjectiveStatusFilter / ObjectiveDateMatch enums. */
export type RelationshipsObjectiveStatus = 'OPEN' | 'CLOSED' | 'ALL';
export type RelationshipsDateMatch = 'FALLS_WITHIN' | 'EXACTLY_EQUALS' | 'ACTIVE_ON';
/** search-api RelationObjectType values for the relationship source. */
export type RelationshipsSourceType = 'OBJECTIVE' | 'KEY_RESULT';

/** Object Type filter option values. */
export const RELATIONSHIPS_SOURCE_TYPES: RelationshipsSourceType[] = ['OBJECTIVE', 'KEY_RESULT'];

/**
 * search-api RelationshipsObjectiveFilterInput (dates are YYYY-MM-DD), plus the `sourceTypes`
 * argument; sourceTypes is only set when exactly one object type is selected.
 */
export interface RelationshipsObjectiveFilter {
  status: RelationshipsObjectiveStatus;
  startDate?: string;
  endDate?: string;
  dateMatch?: RelationshipsDateMatch;
  sourceTypes?: RelationshipsSourceType[];
  teamId?: string;
  /** only sent with a team; the caller drops it for dynamic teams */
  includeTeamOrgTree?: boolean;
}

/** Filters criterion: multi-selects carry `values`, single-selects `value`; time periods dates. */
interface FilterCriterion {
  fieldName: string;
  operator?: string;
  values?: { value?: unknown; [key: string]: unknown }[];
  value?: { value?: unknown } | null;
}

interface FilterValues {
  group?: { criteria?: FilterCriterion[] };
}

const EX_DATE_TO_DATE_MATCH: Record<number, RelationshipsDateMatch> = {
  [okrDetailConstants.EX_DATE_SPAN_PERIOD]: 'FALLS_WITHIN',
  [okrDetailConstants.EX_DATE_EXACT_MATCH]: 'EXACTLY_EQUALS',
  [okrDetailConstants.EX_DATE_ACTIVE_ON]: 'ACTIVE_ON',
};

export const DEFAULT_RELATIONSHIPS_FILTER: RelationshipsObjectiveFilter = { status: 'ALL' };

const toIsoDate = (date: string): string | undefined => {
  const parsed = moment(date, 'MM/DD/YYYY', true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};

const criterionValues = (values: FilterValues | undefined, fieldName: string): unknown[] => (
  values?.group?.criteria?.find((c) => c.fieldName === fieldName)?.values?.map((v) => v.value) ?? []
);

/** The selected team id of the single-select team criterion, if any. */
export const selectedTeamId = (values?: FilterValues): string | undefined => {
  const value = values?.group?.criteria?.find((c) => c.fieldName === 'team')?.value?.value;
  return value === undefined || value === null || value === '' ? undefined : String(value);
};

/** Maps shared Filters values (status, object type, time period, team) to the search-api filter. */
export const toRelationshipsFilter = (
  values?: FilterValues,
  includeTeamOrgTree = false,
): RelationshipsObjectiveFilter => {
  const statusValues = criterionValues(values, 'status');
  const open = statusValues.includes(ALIGNMENT_FILTER_STATUS.OPEN);
  const closed = statusValues.includes(ALIGNMENT_FILTER_STATUS.CLOSED);
  let status: RelationshipsObjectiveStatus = 'ALL';
  if (open !== closed) {
    status = open ? 'OPEN' : 'CLOSED';
  }

  const { startDate, endDate, exDate } = mapTimePeriodCriterionToParams(values);
  const filter: RelationshipsObjectiveFilter = { status };
  // Like status: both or neither selected means no type filter.
  const sourceTypes = RELATIONSHIPS_SOURCE_TYPES.filter(
    (type) => criterionValues(values, 'objectType').includes(type),
  );
  if (sourceTypes.length === 1) filter.sourceTypes = sourceTypes;
  const isoStart = startDate ? toIsoDate(startDate) : undefined;
  const isoEnd = endDate ? toIsoDate(endDate) : undefined;
  if (isoStart) filter.startDate = isoStart;
  if (isoEnd) filter.endDate = isoEnd;
  if (isoStart || isoEnd) {
    filter.dateMatch = EX_DATE_TO_DATE_MATCH[Number(exDate)] ?? 'FALLS_WITHIN';
  }
  const teamId = selectedTeamId(values);
  if (teamId) {
    filter.teamId = teamId;
    if (includeTeamOrgTree) filter.includeTeamOrgTree = true;
  }
  return filter;
};

/** CSV query params for /downloads?option=csv_relationships (search-api lower-case values). */
export const toRelationshipsCsvParams = (
  filter: RelationshipsObjectiveFilter,
): Record<string, string> => {
  const params: Record<string, string> = { objectiveStatus: filter.status.toLowerCase() };
  if (filter.startDate) params.startDate = filter.startDate;
  if (filter.endDate) params.endDate = filter.endDate;
  if (filter.dateMatch) params.dateMatch = filter.dateMatch.toLowerCase();
  if (filter.sourceTypes?.length) {
    params.sourceTypes = filter.sourceTypes.map((type) => type.toLowerCase()).join(',');
  }
  if (filter.teamId) params.teamId = filter.teamId;
  if (filter.teamId && filter.includeTeamOrgTree) params.includeTeamOrgTree = '1';
  return params;
};
